import { Button, Card, Popover, Typography } from 'antd'
import { CopyOutlined, EditOutlined, FileTextOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import AvatarGroup from '../DayView/AvatarGroup'
import { ScheduledEvent, CrewMember, RepeatingEventConfig, SchedulingJob } from '../scheduleApi'
import { useState } from 'react'
import ManageScheduledEvent from './Modals/ManageScheduledEvent'
import CreateEventFromJobModal from '../CreateEvent/CreateEventFromJobModal'
import { useRouteByIdQuery } from '../../RouteManagement/routingQueries'

const { Text } = Typography

type Props = {
  event: ScheduledEvent
  allCrew: CrewMember[]
  repeatingEventConfig: RepeatingEventConfig | null
  selectedDate: Dayjs
  readOnly?: boolean
}

const JobDrawerSummary = ({ event, allCrew, repeatingEventConfig, selectedDate, readOnly }: Props) => {
  const [isManageScheduledEventOpen, setIsManageScheduledEventOpen] = useState(false)
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)

  const { data: route } = useRouteByIdQuery(event?.routeId || 0)

  const getRepeatingText = () => {
    if (!repeatingEventConfig) {
      return null
    }

    const config = (repeatingEventConfig as any).repeatingEventConfig || repeatingEventConfig
    const eventDay = dayjs(event.start).format('dddd')

    const repeatUnits = config.repeatUnits

    if (repeatUnits === 0 || repeatUnits === 'day') {
      return `Daily`
    } else if (repeatUnits === 1 || repeatUnits === 'week') {
      const repeatCount = config.repeatCount || 1
      if (repeatCount === 1) {
        return `Weekly on ${eventDay}s`
      } else if (repeatCount === 2) {
        return `Bi-Weekly on ${eventDay}s`
      } else {
        return `Weekly on ${eventDay}s, Every ${repeatCount} Weeks`
      }
    } else if (repeatUnits === 2 || repeatUnits === 'month') {
      const place = config.repeatPlace || ''
      return `Monthly on the ${place} ${eventDay}`
    } else if (repeatUnits === 4 || repeatUnits === 'year') {
      const place = config.repeatPlace || ''
      const month = config.repeatMonth || ''
      if (place && month) {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
        const monthName = monthNames[parseInt(month) - 1] || ''
        return `Annually on the ${place} ${eventDay} of ${monthName}`
      }
      return `Annually on ${dayjs(event.start).format('MMMM D')}`
    }

    return null
  }

  const repeatingText = getRepeatingText()

  return <>
    <div style={{ marginBottom: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <Text style={{ fontSize: '14px' }}>
            Event Summary
          </Text>
          {!readOnly && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                icon={<CopyOutlined />}
                onClick={() => setIsDuplicateModalOpen(true)}
              >
                Duplicate
              </Button>
              <Button
                icon={<EditOutlined />}
                onClick={() => setIsManageScheduledEventOpen(true)}
              >
                Manage
              </Button>
            </div>
          )}
        </div>
      </div>

      <Card>
        {event.routeId !== null && route && route?.defaultScheduleConfig !== null &&
          <div style={{
            paddingBottom: '12px',
            fontSize: '16px',
            borderBottom: '1px solid #f0f0f0',
            marginBottom: '12px'
          }}>
            <Text strong>{route.name}</Text>
            <div style={{ fontSize: '14px', color: '#8c8c8c' }}>Associated Route</div>
            <br />
            <div style={{ fontSize: '14px' }}>
              <div>
                <span style={{ fontWeight: 500, color: '#8c8c8c' }}>Starts</span> <span style={{ fontWeight: 600 }}>
                  {route.defaultScheduleConfig?.startDate && dayjs(route.defaultScheduleConfig.startDate).isValid()
                    ? dayjs(route.defaultScheduleConfig.startDate).format('MMM D, YYYY')
                    : ''}
                </span>
              </div>
              <div>
                <span style={{ fontWeight: 500, color: '#8c8c8c' }}>Repeats</span> <span style={{ fontWeight: 500 }}>
                  {route.defaultScheduleConfig.repeatUnits === 'month'
                    ? `Monthly on the ${(() => {
                      const repeatPlace = route.defaultScheduleConfig?.repeatPlace || ''
                      const parts = repeatPlace.split(',').map((part: string) => part.trim())
                      const orderedParts = ['1st', '2nd', '3rd', '4th', 'Last']
                      const sortedParts = orderedParts.filter(order =>
                        parts.some((part: string) => part.toLowerCase().includes(order.toLowerCase()))
                      )
                      return sortedParts.length > 0 ? sortedParts.join(', ') : repeatPlace
                    })()} ${(route.defaultScheduleConfig?.dayOfWeek || '').charAt(0).toUpperCase() + (route.defaultScheduleConfig?.dayOfWeek || '').slice(1).toLowerCase()}`
                    : route.defaultScheduleConfig.repeatUnits === 'year'
                        ? `Annually on the ${(() => {
                          const repeatPlace = route.defaultScheduleConfig?.repeatPlace || ''
                          const parts = repeatPlace.split(',').map((part: string) => part.trim())
                          const orderedParts = ['1st', '2nd', '3rd', '4th', 'Last']
                          const sortedParts = orderedParts.filter(order =>
                            parts.some((part: string) => part.toLowerCase().includes(order.toLowerCase()))
                          )
                          return sortedParts.length > 0 ? sortedParts.join(', ') : repeatPlace
                        })()} ${(route.defaultScheduleConfig?.dayOfWeek || '').charAt(0).toUpperCase() + (route.defaultScheduleConfig?.dayOfWeek || '').slice(1).toLowerCase()} of ${(() => {
                          const repeatMonth = route.defaultScheduleConfig?.repeatMonth || ''
                          const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
                          const sortedMonthNumbers = repeatMonth.split(',').map((num: string) => parseInt(num.trim())).filter((num: number) => !isNaN(num)).sort((a: number, b: number) => a - b)
                          const monthNamesList = sortedMonthNumbers.map((num: number) => monthNames[num - 1]).filter(Boolean)
                          return monthNamesList.join(', ')
                        })()}`
                        : `Every ${route.defaultScheduleConfig?.repeatCount || 1} Weeks`}
                </span>
              </div>
              {route.defaultScheduleConfig?.endType === 'on' && (
                <div>
                  <span style={{ fontWeight: 500, color: '#8c8c8c' }}>Ends</span> <span style={{ fontWeight: 600 }}>
                    {route.defaultScheduleConfig?.endDate && dayjs(route.defaultScheduleConfig.endDate).isValid()
                      ? dayjs(route.defaultScheduleConfig.endDate).format('MMM D, YYYY')
                      : ''}
                  </span>
                </div>
              )}
              {route.defaultScheduleConfig?.endType === 'after' && (
                <div>
                  <span style={{ fontWeight: 600 }}>After {route.defaultScheduleConfig?.occurrences || route.defaultScheduleConfig?.repeatCount || 1} times</span>
                </div>
              )}
            </div>
          </div>
        }

        <div style={{
          padding: '12px 0',
          fontSize: '16px',
          fontWeight: '500',
          borderBottom: '1px solid #f0f0f0',
          marginBottom: '24px'
        }}>
          <div style={{ fontSize: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              {event.allDay || dayjs(event.end).diff(dayjs(event.start), 'hour') >= 24
                ? `${selectedDate.format('ddd • MMM D YYYY')}${event.allDay ? ' | All Day Event' : ''}`
                : `${selectedDate.format('ddd • MMM D YYYY')} | ${dayjs(event.start).format('h:mm A')} - ${dayjs(event.end).format('h:mm A')}`}
            </span>
            {(event.notes ?? event.schedulingNotes) && (
              <Popover content={<div style={{ maxWidth: '300px', whiteSpace: 'pre-wrap' }}>{event.notes ?? event.schedulingNotes}</div>} title="Scheduling Notes" trigger="hover">
                <FileTextOutlined style={{ fontSize: '16px', color: '#1890ff', cursor: 'pointer' }} />
              </Popover>
            )}
          </div>
          {dayjs(event.end).diff(dayjs(event.start), 'hour') >= 24 ? (
            <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
              <div>Multi-Day Event</div>
              <div>
                Starts on: {dayjs(event.start).format(event.allDay ? 'MMM D, YYYY' : 'MMM D, YYYY h:mm A')} • Ends on: {dayjs(event.end).format(event.allDay ? 'MMM D, YYYY' : 'MMM D, YYYY h:mm A')}
              </div>
            </div>
          ) : repeatingText ? (
            <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
              {repeatingText}
              {repeatingEventConfig && (() => {
                const config = (repeatingEventConfig as any).repeatingEventConfig || repeatingEventConfig
                if (config.endDate) {
                  return ` • Ends on ${dayjs(config.endDate).format('MMM D, YYYY')}`
                }
                return null
              })()}
            </div>
          ) : null}
        </div>

        {/* Assigned Crew */}
        <div style={{ marginBottom: '24px' }}>
          <Text style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}>
            Assigned Crew
          </Text>
          {allCrew.length > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AvatarGroup crewForemanEmail={event.crewForemanEmail} crewMemberIds={event.crewMemberIds} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {allCrew.map((member, index) => (
                  <Text strong key={member?.id} style={{ fontSize: '16px' }}>
                    {member?.name}{index < allCrew.length - 1 ? ', ' : ''}
                  </Text>
                ))}
              </div>
            </div>
          ) : (
            <Text style={{ color: '#999', fontStyle: 'italic' }}>
              No assigned crew
            </Text>
          )}
        </div>

        {/* Work Areas */}
        <div>
          <Text style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}>
            Work Areas
          </Text>
          {event.workAreaIds !== null && event.workAreaIds !== '' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {event.workAreaNames?.split(',').map((workareaName, index) => (
                <Text strong key={index} style={{ fontSize: '16px', color: '#333' }}>
                  {workareaName}
                </Text>
              ))}
            </div>
          ) : (
            <Text strong style={{ color: '#999', fontStyle: 'italic' }}>No work areas assigned</Text>
          )}
        </div>
      </Card>
    </div>
    <ManageScheduledEvent open={isManageScheduledEventOpen} onClose={() => setIsManageScheduledEventOpen(false)} event={event} repeatingEventConfig={repeatingEventConfig} />
    <CreateEventFromJobModal
      open={isDuplicateModalOpen}
      onClose={() => setIsDuplicateModalOpen(false)}
      job={{
        jobId: event.jobId,
        jobName: event.jobName,
        customerId: event.customerId || 0,
        customerDisplayName: event.customerDisplayName || '',
        active: true,
        allDivisionIds: event.projectAllDivisionIds || '',
        type: event.projectType || 0,
        address1: event.customerAddress1 || '',
        address2: event.customerAddress2 || null,
        city: event.customerCity || '',
        state: event.customerState || '',
        zip: event.customerZip || '',
        calendarEventCount: 0,
        hasApprovedWorkareas: false,
        approvedWorkAreas: [],
      } as SchedulingJob}
      selectedDate={selectedDate}
      initialEvent={event}
    />
  </>
}

export default JobDrawerSummary